import React, { useContext, useMemo } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { format, isFuture, parse } from 'date-fns';
import * as R from 'ramda';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CallMissedOutgoingRoundedIcon from '@mui/icons-material/CallMissedOutgoingRounded';
import CallMissedRoundedIcon from '@mui/icons-material/CallMissedRounded';
import MoveToInboxRoundedIcon from '@mui/icons-material/MoveToInboxRounded';
import NextWeekRoundedIcon from '@mui/icons-material/NextWeekRounded';
import ThreeSixtyRoundedIcon from '@mui/icons-material/ThreeSixtyRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ZoomOutMapRoundedIcon from '@mui/icons-material/ZoomOutMapRounded';
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded';
import GpsOffRoundedIcon from '@mui/icons-material/GpsOffRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import { useUserRolePermissions } from 'hooks';

// Utils
import { DRAWER_TYPES } from 'utils/constants';
import { userRoles } from 'utils/currentUserRoles';
import { AuthContext } from 'authentication/AuthContext';
import {
  FUTURE_TRANSFER_DATE_MSG,
  OLD_FUTURE_TRANSFER_DATE_MSG,
  PTO_STATUS,
} from 'feature/inventory-management/product-transfer-order/utils';

// Components
import { ChainRounded, MIconMenu } from 'library';
import { MAlert } from 'common';
import { DrawerContext } from 'common/drawer/DrawerContext';
import ActionButtons from './ActionButtons';

import ViewPanel from '../../view-panel/ViewPanel.connector';
import EditPtoPanel from '../../edit-panel/EditPanel.connector';
import CreatePtoWizard from '../../create-wizard/CreateWizard.connector';

const isIcon = {
  color: 'primary.dark',
  height: 20,
  marginRight: 10,
  width: 20,
};

const affiliateStyles = {
  height: 16,
  transform: 'rotate(45deg)',
  width: 16,
};

const iconStyle = {
  color: 'primary.dark',
  height: 20,
  marginRight: 1,
  width: 20,
};

const OpenPTOCard = ({ resetCreatePto, setCurrentPtoDialogueOpen, data }) => {
  const { isInventoryOwner } = useUserRolePermissions();
  const { roles } = useContext(AuthContext);
  const { setDrawerComponentProps } = useContext(DrawerContext);

  const {
    affiliateIsOriginator,
    custody,
    custodyMessage,
    dataCompany,
    errorMessage,
    inventorySelection,
    inventorySelectionMessage,
    location,
    origin,
    originMessage,
    originator,
    product,
    ptoNumber,
    recipient,
    reconsignmentControl,
    reconsignmentControlMessage = '',
    status,
    transferDate,
    volume,
  } = data;

  const transferDateInFuture = R.and(
    R.equals(status, PTO_STATUS.approved),
    isFuture(parse(transferDate, 'M/d/yyyy', new Date()))
  );

  const onViewDetails = () =>
    setDrawerComponentProps({
      Component: props => <ViewPanel ptoNumber={ptoNumber} {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.viewPtoDetails,
    });

  const onEditPto = () =>
    setDrawerComponentProps({
      Component: props => <EditPtoPanel isEdit ptoNumber={ptoNumber} {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.editPto,
    });

  const onCopyPto = () =>
    setDrawerComponentProps({
      Component: props => <CreatePtoWizard copyPtoNumber={ptoNumber} isCopyToNew {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.copyPto,
    });

  const menuItems = useMemo(() => {
    const statusNotApprovedOrRejected = R.not(
      R.includes(status, [PTO_STATUS.approvalNeeded, PTO_STATUS.rejected])
    );
    const statusCancelledExpiredOrRejected = R.includes(status, [
      PTO_STATUS.cancelled,
      PTO_STATUS.expired,
      PTO_STATUS.rejected,
    ]);
    const affiliateIsOriginatorWithProductOwnershipManageRole = R.and(
      affiliateIsOriginator,
      R.includes(userRoles.PRODUCT_OWNERSHIP_MANAGE, roles)
    );
    const showEdit = R.and(
      statusNotApprovedOrRejected,
      affiliateIsOriginatorWithProductOwnershipManageRole
    );
    const showCopy = R.and(
      statusCancelledExpiredOrRejected,
      affiliateIsOriginatorWithProductOwnershipManageRole
    );

    const view = [
      {
        label: 'View Order',
        onClick: onViewDetails,
        value: DRAWER_TYPES.viewPtoDetails,
      },
    ];

    const edit = [
      {
        label: 'Edit Order',
        onClick: onEditPto,
        value: DRAWER_TYPES.editPto,
      },
    ];

    const copy = [
      {
        label: 'Copy Order',
        onClick: onCopyPto,
        value: DRAWER_TYPES.copyPto,
      },
    ];

    let menu = [...view];
    if (showEdit) {
      menu = [...menu, ...edit];
    }
    if (showCopy) {
      menu = [...menu, ...copy];
    }
    if (showEdit && showCopy) {
      menu = [...menu, ...edit, ...copy];
    }

    return menu;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateIsOriginator, roles, status]);

  const inventorySelectionIcon = R.cond([
    [R.equals('AVAILABLE'), R.always(<MoveToInboxRoundedIcon sx={iconStyle} />)],
    [R.equals('INTRANSIT'), R.always(<NextWeekRoundedIcon sx={iconStyle} />)],
    [R.equals('EITHER'), R.always(<ThreeSixtyRoundedIcon sx={iconStyle} />)],
  ]);

  const reconsignmentControlIcon = R.cond([
    [R.equals('EITHER'), R.always(<ZoomOutMapRoundedIcon sx={iconStyle} />)],
    [R.equals('CONTROL'), R.always(<LockOutlinedIcon sx={iconStyle} />)],
    [R.T, R.always(<LockOpenOutlinedIcon sx={iconStyle} />)],
  ]);

  const originIcon = R.cond([
    [R.equals('Custom'), R.always(<GpsOffRoundedIcon sx={iconStyle} />)],
    [R.equals('Any'), R.always(<GpsFixedRoundedIcon sx={iconStyle} />)],
  ]);

  const custodyIcon = R.cond([
    [R.equals('Direct'), R.always(<LinkOffRoundedIcon sx={iconStyle} />)],
    [R.equals('Chained'), R.always(<ChainRounded sx={isIcon} />)],
  ]);

  return (
    <Card
      sx={{
        borderRadius: 0.5,
        boxShadow: 5,
        margin: 1,
        minHeight: 234,
        width: 304,
      }}
    >
      <CardContent
        sx={{
          '&:last-child': {
            paddingBottom: 2,
          },
        }}
      >
        <Typography variant="body2">#{ptoNumber}</Typography>
        <Stack justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Box display="flex" justifyContent="space-between">
            <Typography
              className="css-heap-redact"
              sx={{
                color: 'primary.dark',
                fontWeight: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 202,
              }}
              variant="h6"
            >
              {affiliateIsOriginator ? recipient : originator}
            </Typography>
            {isInventoryOwner && <MIconMenu items={menuItems} />}
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Box alignItems="center" display="flex">
              {affiliateIsOriginator ? (
                <CallMissedRoundedIcon color="text.secondary" sx={affiliateStyles} />
              ) : (
                <CallMissedOutgoingRoundedIcon color="text.secondary" sx={affiliateStyles} />
              )}
              <Typography className="css-heap-redact" sx={{ ml: 0.5 }} variant="body2">
                {affiliateIsOriginator ? originator : recipient}
              </Typography>
            </Box>
            <Typography variant="body2">{dataCompany}</Typography>
          </Box>
        </Stack>
        <Divider />
        <Stack spacing={1} sx={{ mb: 2, mt: 1, mx: 0 }}>
          <Box display="flex" justifyContent="space-between">
            <Typography className="css-heap-redact" color="textPrimary" variant="body2">
              {volume}
            </Typography>
            <Typography color="textPrimary" variant="body2">
              {location}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography className="css-heap-redact" color="textPrimary" variant="body2">
              {product}
            </Typography>
            <Typography variant="body2">{format(new Date(transferDate), 'M/d/yyyy')}</Typography>
          </Box>
          <Stack spacing={2}>
            <Box alignItems="center" display="flex">
              {inventorySelectionIcon(inventorySelection)}
              <Typography variant="body2">{inventorySelectionMessage}</Typography>
            </Box>
            <Box alignItems="center" display="flex">
              {reconsignmentControlIcon(reconsignmentControl)}
              <Typography variant="body2">{reconsignmentControlMessage}</Typography>
            </Box>
            <Box alignItems="center" display="flex">
              {originIcon(origin)}
              <Typography variant="body2">{originMessage}</Typography>
            </Box>
            <Box display="flex">
              {custodyIcon(custody)}
              <Typography variant="body2">{custodyMessage}</Typography>
            </Box>
          </Stack>
        </Stack>

        {transferDateInFuture && (
          <MAlert
            iconType={<UpdateRoundedIcon />}
            message={OLD_FUTURE_TRANSFER_DATE_MSG}
            severity="error"
            sx={{ mb: 2 }}
            variant="outlined"
          />
        )}
        {errorMessage && status !== PTO_STATUS.scheduled && (
          <MAlert message={errorMessage} severity="error" sx={{ mb: 2 }} />
        )}
        {status === PTO_STATUS.scheduled && (
          <MAlert
            iconType={<InfoRoundedIcon sx={{ color: 'graphic.main', margin: 'auto' }} />}
            message={FUTURE_TRANSFER_DATE_MSG}
            severity="info"
            sx={{
              bgcolor: 'success.lighter',
              color: 'text.secondary',
              mb: 2,
            }}
            variant="standard"
          />
        )}

        <Box columnGap="16px" display="flex" justifyContent="center" width="100%">
          {isInventoryOwner && (
            <ActionButtons
              affiliateIsOriginator={affiliateIsOriginator}
              onCopyPto={onCopyPto}
              onEditPto={onEditPto}
              onViewDetails={onViewDetails}
              ptoNumber={ptoNumber}
              setCurrentPtoDialogueOpen={setCurrentPtoDialogueOpen}
              status={status}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

OpenPTOCard.propTypes = {
  data: shape({
    affiliateIsOriginator: bool.isRequired,
    affiliateIsRecipient: bool.isRequired,
    custody: string.isRequired,
    custodyMessage: string.isRequired,
    dataCompany: string.isRequired,
    errorMessage: string,
    inventorySelection: string.isRequired,
    inventorySelectionMessage: string.isRequired,
    location: string.isRequired,
    origin: string.isRequired,
    originMessage: string.isRequired,
    originator: string.isRequired,
    product: string.isRequired,
    ptoNumber: number.isRequired,
    recipient: string.isRequired,
    reconsignmentControl: string.isRequired,
    reconsignmentControlMessage: string.isRequired,
    status: string.isRequired,
    transferDate: string.isRequired,
    volume: string.isRequired,
  }).isRequired,
  resetCreatePto: func.isRequired,
  setCurrentPtoDialogueOpen: func.isRequired,
};

export default OpenPTOCard;
